// theme.js - Glassmorphism Theme
// Light Mode: Bluish Glass Effect (matching #A7BFE8 to #6190E8 gradient)
// Dark Mode: Blue/Black/Grayish Glass Effect
import { createTheme } from "@mui/material/styles";

// ============================================
// LIGHT THEME - Bluish Glassmorphism
// ============================================
export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2563EB",      // Vibrant blue that contrasts with blue background
      light: "#3B82F6",
      dark: "#1D4ED8",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#8B5CF6",      // Purple
      light: "#A78BFA",
      dark: "#7C3AED",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#10B981",      // Emerald Green
      light: "#34D399",
      dark: "#059669",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#F59E0B",      // Amber
      light: "#FBBF24",
      dark: "#D97706",
      contrastText: "#1F2937",
    },
    error: {
      main: "#EF4444",      // Red
      light: "#F87171",
      dark: "#DC2626",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#0EA5E9",      // Sky Blue
      light: "#38BDF8",
      dark: "#0284C7",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "linear-gradient(to top, #A7BFE8, #6190E8)",   // Your new gradient
      paper: "rgba(255, 255, 255, 0.75)",  // More opaque for better contrast
    },
    text: {
      primary: "#0F172A",   // stronger contrast
      secondary: "#334155",
      disabled: "#64748B",
    },
    divider: "rgba(30, 64, 175, 0.25)",  // Purple tint to complement blue
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
      color: "#1E293B",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      color: "#1E293B",
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.3,
      color: "#1E293B",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1E293B",
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#1E293B",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
      color: "#1E293B",
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.9375rem",
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgba(30, 41, 59, 0.05)",
    "0 1px 3px 0 rgba(30, 41, 59, 0.1), 0 1px 2px 0 rgba(30, 41, 59, 0.06)",
    "0 4px 6px -1px rgba(30, 41, 59, 0.1), 0 2px 4px -1px rgba(30, 41, 59, 0.06)",
    "0 8px 32px rgba(30, 41, 59, 0.15)",  // Deeper shadows for contrast
    "0 12px 40px rgba(30, 41, 59, 0.2)",
    "0 25px 50px -12px rgba(30, 41, 59, 0.25)",
    ...Array(18).fill("none"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // background: '#ffffff',
background: 'radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(148, 187, 233, 1) 100%)',
          backgroundAttachment: "fixed",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
        },
        "*::-webkit-scrollbar": {
          width: "10px",
          height: "10px",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          borderRadius: "10px",
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(37, 99, 235, 0.5)",  // Primary blue
          borderRadius: "10px",
          border: "2px solid transparent",
          backgroundClip: "content-box",
          "&:hover": {
            backgroundColor: "rgba(37, 99, 235, 0.7)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(99, 102, 241, 0.2)",
          boxShadow: "0 8px 32px rgba(30, 41, 59, 0.15)",
          color: "#1E293B",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRight: "1px solid rgba(99, 102, 241, 0.2)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.88)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(15px)",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(30, 41, 59, 0.15)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 40px rgba(30, 41, 59, 0.2)",
            borderColor: "rgba(255, 255, 255, 0.6)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: 20,
          boxShadow: "0 8px 32px rgba(30, 41, 59, 0.15)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 20px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        contained: {
          background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
          boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.4)",
          color: "#FFFFFF",
          "&:hover": {
            background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
            boxShadow: "0 6px 20px 0 rgba(37, 99, 235, 0.5)",
            transform: "translateY(-2px)",
            outline: "2px solid rgba(255,255,255,0.4)",
            outlineOffset: "1px",
          },
          "&.Mui-disabled": {
            background: "rgba(37, 99, 235, 0.3)",
            color: "rgba(255, 255, 255, 0.5)",
          }
        },
        outlined: {
          borderWidth: "1.5px",
          borderColor: "rgba(99, 102, 241, 0.4)",
          color: "#2563EB",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          "&:hover": {
            borderWidth: "1.5px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderColor: "rgba(99, 102, 241, 0.6)",
          },
        },
        text: {
          color: "#2563EB",
          "&:hover": {
            backgroundColor: "rgba(37, 99, 235, 0.1)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: "all 0.2s ease",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(10px)",
          color: "#1E293B",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            transform: "scale(1.05)",
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: "4px 8px",
          transition: "all 0.2s ease",
          color: "#1E293B",
          "&:hover": {
            backgroundColor: "rgba(37, 99, 235, 0.1)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(37, 99, 235, 0.15)",
            color: "#2563EB",
            "&:hover": {
              backgroundColor: "rgba(37, 99, 235, 0.2)",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "20%",
              bottom: "20%",
              width: "4px",
              borderRadius: "0 4px 4px 0",
              background: "linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)",
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "rgba(255, 255, 255, 0.95)",  // More opaque for readability
          backdropFilter: "blur(10px)",
          transition: "all 0.2s ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(37, 99, 235, 0.4)",
            borderWidth: "1.5px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(99, 102, 241, 0.5)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#2563EB",
            borderWidth: "2px",
          },
        },
        input: {
          color: "#1E293B",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          backdropFilter: "blur(10px)",
        },
        filled: {
          background: "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)",
          color: "#2563EB",
          border: "1px solid rgba(37, 99, 235, 0.3)",
        },
        outlined: {
          borderColor: "rgba(37, 99, 235, 0.3)",
          color: "#2563EB",
        }
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          fontWeight: 700,
          color: "#1E293B",
          backdropFilter: "blur(10px)",
        },
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          borderColor: "rgba(99, 102, 241, 0.2)",
          color: "#1E293B",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(30px)",
          WebkitBackdropFilter: "blur(30px)",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          boxShadow: "0 20px 50px rgba(30, 41, 59, 0.25)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow: "0 8px 32px rgba(30, 41, 59, 0.2)",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(37, 99, 235, 0.1)",
          borderRadius: 4,
        },
        bar: {
          background: "linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)",
          borderRadius: 4,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: "#2563EB",
        },
      },
    },
  },
});

// ============================================
// DARK THEME - Blue/Black/Grayish Glassmorphism
// ============================================
export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#60A5FA",      // Lighter blue for dark mode
      light: "#93C5FD",
      dark: "#3B82F6",
      contrastText: "#0F172A",
    },
    secondary: {
      main: "#A78BFA",      // Lighter purple
      light: "#C4B5FD",
      dark: "#8B5CF6",
      contrastText: "#0F172A",
    },
    success: {
      main: "#34D399",      // Lighter green
      light: "#6EE7B7",
      dark: "#10B981",
      contrastText: "#0F172A",
    },
    warning: {
      main: "#FBBF24",      // Lighter amber
      light: "#FCD34D",
      dark: "#F59E0B",
      contrastText: "#0F172A",
    },
    error: {
      main: "#F87171",      // Lighter red
      light: "#FCA5A5",
      dark: "#EF4444",
      contrastText: "#0F172A",
    },
    info: {
      main: "#38BDF8",      // Lighter sky blue
      light: "#7DD3FC",
      dark: "#0EA5E9",
      contrastText: "#0F172A",
    },
    background: {
      default: "linear-gradient(156.2deg, rgb(1, 1, 1) 14.8%, rgba(32,104,177,1) 68.1%, rgb(169, 192, 218) 129%)",
      paper: "rgba(30, 41, 59, 0.6)",  // More transparent for glass effect
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#E2E8F0",
      disabled: "#94A3B8",
    },
    divider: "rgba(148, 163, 184, 0.3)",
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
      color: "#F1F5F9",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      color: "#F1F5F9",
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.3,
      color: "#F1F5F9",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F1F5F9",
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#F1F5F9",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
      color: "#F1F5F9",
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.9375rem",
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    "0 8px 32px rgba(0, 0, 0, 0.3)",  // Stronger shadows for dark mode
    "0 12px 40px rgba(0, 0, 0, 0.4)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    ...Array(18).fill("none"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: "linear-gradient(156.2deg, rgb(1, 1, 1) 14.8%, rgba(32,104,177,1) 68.1%, rgb(169, 192, 218) 129%)",
          backgroundAttachment: "fixed",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
        },
        "*::-webkit-scrollbar": {
          width: "10px",
          height: "10px",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          borderRadius: "10px",
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(96, 165, 250, 0.5)",  // Primary blue for dark mode
          borderRadius: "10px",
          border: "2px solid transparent",
          backgroundClip: "content-box",
          "&:hover": {
            backgroundColor: "rgba(96, 165, 250, 0.7)",
          },
        },
      },
    },
    // ... keep all dark theme component overrides as they were ...
    // (The dark theme component overrides remain the same as before)
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          color: "#F1F5F9",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRight: "1px solid rgba(148, 163, 184, 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(148, 163, 184, 0.1)",
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)",
            borderColor: "rgba(148, 163, 184, 0.2)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(148, 163, 184, 0.1)",
          borderRadius: 20,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 20px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        contained: {
          background: "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)",
          boxShadow: "0 4px 14px 0 rgba(96, 165, 250, 0.35)",
          "&:hover": {
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
            boxShadow: "0 6px 20px 0 rgba(96, 165, 250, 0.45)",
            transform: "translateY(-2px)",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          borderColor: "rgba(148, 163, 184, 0.4)",
          color: "#F1F5F9",
          backgroundColor: "rgba(30, 41, 59, 0.3)",
          "&:hover": {
            borderWidth: "1.5px",
            backgroundColor: "rgba(30, 41, 59, 0.5)",
            borderColor: "rgba(148, 163, 184, 0.6)",
          },
        },
        text: {
          color: "#60A5FA",
          "&:hover": {
            backgroundColor: "rgba(96, 165, 250, 0.08)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: "all 0.2s ease",
          backgroundColor: "rgba(30, 41, 59, 0.3)",
          backdropFilter: "blur(10px)",
          color: "#F1F5F9",
          "&:hover": {
            backgroundColor: "rgba(30, 41, 59, 0.5)",
            transform: "scale(1.05)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: "rgba(30, 41, 59, 0.5)",
          backdropFilter: "blur(10px)",
          transition: "all 0.2s ease",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(148, 163, 184, 0.4)",
            borderWidth: "1.5px",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(148, 163, 184, 0.6)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#60A5FA",
            borderWidth: "2px",
          },
        },
        input: {
          color: "#F1F5F9",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          backdropFilter: "blur(10px)",
        },
        filled: {
          backgroundColor: "rgba(96, 165, 250, 0.15)",
          color: "#60A5FA",
          border: "1px solid rgba(96, 165, 250, 0.3)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(30, 41, 59, 0.5)",
          borderColor: "rgba(148, 163, 184, 0.3)",
          color: "#F1F5F9",
        },
        head: {
          backgroundColor: "rgba(30, 41, 59, 0.7)",
          fontWeight: 700,
          color: "#F1F5F9",
          backdropFilter: "blur(10px)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(30, 41, 59, 0.9)",
          backdropFilter: "blur(30px)",
          WebkitBackdropFilter: "blur(30px)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "rgba(30, 41, 59, 0.9)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(148, 163, 184, 0.1)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(148, 163, 184, 0.3)",
        },
      },
    },
  },
});